import {
  actionMapViewChanged,
  actionMapCursorUpdated,
  actionMapUpdateZoomFeatures,
  actionMapUpdateActiveFeature,
  actionMapSourceDataLoaded,
} from "./MapRedux";
import {
  mapViewToFloors,
  getUniqueFeatures,
  getMapView,
  getActiveFeature,
  featuresToFloors,
  checkClick,
} from "./maputils";
import {
  actionAppBarFloorsInViewUpdated,
  actionAppBarFloorsInActiveBuildingUpdated,
  actionAppBarRenderedFloorUpdated,
} from "../AppBar";
import {
  actionInfoDataAddedBackground,
  actionInfoDataAddedForeground,
} from "../Info";
import { actionAuthMapError } from "../Auth";
import {
  actionSheetRoomClicked,
  actionSheetPointClicked,
} from "../Spreadsheet";
import { actionDrawUpdateClickCounter, actionDrawRoomSelect } from "../Draw";
import debounced from "lodash/debounce";
import { actionUpdateMapviewHistory } from "../App";
import { actionPhotoGetTargetRoom } from "../Photo";
import { actionCustomLayerSelectedRoomUpdated } from "../CustomLayer";

export const mapClickHandler = (map, dispatch) => (event) => {
  const features = map.queryRenderedFeatures(event.point);

  if (
    features &&
    Array.isArray(features) &&
    features.length > 0 &&
    features[0].layer.id.indexOf("sheet") === 0
  ) {
    handleCustomClick(features, dispatch);
  }

  dispatch(actionDrawUpdateClickCounter());
  dispatch(actionMapUpdateActiveFeature(getActiveFeature(event.features)));
};

export const mapTouchHandler = (map, dispatch) => (event) => {
  // touchend "click" event for custom, building, and room touches
  const touchEndFunction = (e) => {
    const features = map.queryRenderedFeatures(e.point);
    const buildingData = getUniqueFeatures(features, "bldrecnbr");
    const roomData = getUniqueFeatures(features, "rmrecnbr");
    const { customClick, roomClick, buildingClick } = checkClick(features);

    if (customClick) {
      handleCustomClick(features, dispatch);
    } else if (roomClick) {
      dispatch(actionInfoDataAddedForeground({ data: roomData }));

      // Allow draw/room_picker to work on touch screen.
      // We get room/floor/building features and only want room here.
      const roomFeatures = features.filter(
        (feature) => feature.sourceLayer === "room",
      );
      if (roomFeatures.length > 0) {
        dispatch(actionDrawRoomSelect(roomFeatures));
      }
    } else if (buildingClick) {
      dispatch(actionInfoDataAddedForeground({ data: buildingData }));
    }

    dispatch(actionDrawUpdateClickCounter());
    dispatch(actionMapUpdateActiveFeature(getActiveFeature(features)));
  };

  // add touchend "click" listener on touchstart
  map.once("touchend", touchEndFunction);

  // remove touchend "click" listener if touchmove occurs
  map.once("touchmove", () => {
    map.off("touchend", touchEndFunction);
  });
};

const handleCustomClick = (features, dispatch) => {
  if (features.length === 0) return;

  const id = parseInt(features[0].layer.id.split("-")[2]);
  const type = features[0].layer.id.split("-")[1];

  if (type !== "point") {
    const { rmrecnbr: selectedRmrecnbr, rmnbr: selectedRmnbr } =
      features[0].properties;
    dispatch(
      actionSheetRoomClicked({
        id,
        selectedRmrecnbr,
        selectedRmnbr,
      }),
    );
  }

  if (type === "point") {
    const feat = features[0];
    const { sheetRow: selectedPoint } = feat.properties;
    dispatch(
      actionSheetPointClicked({
        id,
        selectedPoint,
      }),
    );
  }
};

export const mapMoveendHandler =
  (map, dispatch, sawFirstRenderedFloor) => (event) => {
    if (!sawFirstRenderedFloor.current) {
      // This handler runs immediately after a move/zoom ends.
      // So, this runs before any new tiles are rendered.
      // Wait until things settle down and then ask about floors.
      map.once("idle", () => {
        const floorFeatures = map.queryRenderedFeatures({
          layers: ["umich-floor"],
        });
        if (floorFeatures.length > 0) {
          sawFirstRenderedFloor.current = true;
          const floor = floorFeatures[0].properties.floor;
          dispatch(actionAppBarRenderedFloorUpdated(floor));
        }
      });
    }
    const mapView = getMapView(map);
    dispatch(actionMapViewChanged(mapView));
    const floors = mapViewToFloors(map);
    dispatch(actionAppBarFloorsInViewUpdated(floors));

    // Don't record history for browser window resizing.
    if (event?.originalEvent?.type !== "resize") {
      dispatch(actionUpdateMapviewHistory(mapView));
    }
  };

export const mapClickBuildingHandler = (map, dispatch) => (event) => {
  const data = getUniqueFeatures(event.features, "bldrecnbr");
  const features = map.queryRenderedFeatures(event.point);
  const { customClick, roomClick } = checkClick(features);

  if (customClick && !roomClick) {
    dispatch(actionInfoDataAddedBackground({ data }));
  } else {
    // skip building info change if room layer clicked
    if (!roomClick) {
      dispatch(actionInfoDataAddedForeground({ data }));
    }
    dispatch(actionMapUpdateActiveFeature(getActiveFeature(event.features)));
    dispatch(
      actionAppBarFloorsInActiveBuildingUpdated(
        featuresToFloors(event.features),
      ),
    );
  }
};

export const mapClickRoomHandler = (map, dispatch) => (event) => {
  const features = map.queryRenderedFeatures(event.point);
  const { customClick } = checkClick(features);
  const data = getUniqueFeatures(event.features, "rmrecnbr");

  if (customClick) {
    dispatch(actionInfoDataAddedBackground({ data }));
  } else {
    dispatch(actionInfoDataAddedForeground({ data }));
    dispatch(actionPhotoGetTargetRoom(data));
    dispatch(actionDrawRoomSelect(event.features));
    dispatch(actionMapUpdateActiveFeature(getActiveFeature(event.features)));
    dispatch(actionCustomLayerSelectedRoomUpdated(data));
  }
};

export const mapMouseoverBuildingHandler = (map, dispatch) =>
  debounced((event) => {
    dispatch(actionMapCursorUpdated({ featureHover: true }));
  }, 10);

export const mapMouseoverRoomHandler = (map, dispatch) =>
  debounced((event) => {
    dispatch(actionMapCursorUpdated({ featureHover: true }));
  }, 10);

export const mapMouseoutBuildingHandler = (map, dispatch) =>
  debounced((event) => {
    dispatch(actionMapCursorUpdated({ featureHover: false }));
  }, 10);

export const mapMouseoutRoomHandler = (map, dispatch) =>
  debounced((event) => {
    dispatch(actionMapCursorUpdated({ featureHover: false }));
  }, 10);

export const mapDbclickBuildingHandler = (map, dispatch) => (event) => {
  // Prevent mapbox's default double-click handler from firing.
  // This avoids generating two "movend" events; which messes
  // up our zoom history.
  event.preventDefault();
  event.originalEvent.preventDefault();

  dispatch(actionMapUpdateZoomFeatures({ data: event.features }));
};

export const mapDbclickRoomHandler = (map, dispatch) => (event) => {
  // Prevent mapbox's default double-click handler from firing.
  // This avoids generating two "movend" events; which messes
  // up our zoom history.
  event.preventDefault();
  event.originalEvent.preventDefault();

  dispatch(actionMapUpdateZoomFeatures({ data: event.features }));
};

export const mapErrorHandler = (map, dispatch) => (event) => {
  // When our JWT expires, mapbox will fail to pull our pbf tiles
  // and we'll get into here.
  if (event.type === "error") {
    // Requests for tiles that don't exist return 403.
    const isTile403Error =
      event.error.status === 403 && event.error.url.indexOf("/tiles/") !== -1;

    // Don't log uninteresting errors.
    if (
      event.error.message.indexOf("Unimplemented") === -1 &&
      !isTile403Error
    ) {
      console.log("map error:", event);
    }

    if (
      event.error.message &&
      event.error.message.indexOf("Failed to fetch") !== -1
    ) {
      // We'll get here on our non-standard deploys like
      // localhost:3000 or https://umich-mgis-has-2.mgiscloud.com/ due to how
      // cross-origin errors work. You are not allowed to see the HTTP status
      // code.
      dispatch(actionAuthMapError());
    } else if (event.error.status === 401) {
      // We'll get here on standard deploys
      dispatch(actionAuthMapError());
    }
  }
};

export const mapClickCustomLayerHandler = (map, dispatch, id) => (event) => {
  const { rmrecnbr: selectedRmrecnbr, rmnbr: selectedRmnbr } =
    event.features[0].properties;
  dispatch(
    actionSheetRoomClicked({
      id,
      selectedRmrecnbr,
      selectedRmnbr,
    }),
  );
};

export const mapDataSourceLoadedHandler = (dispatch) => (event) => {
  if (
    event.isSourceLoaded &&
    event.sourceId &&
    event.sourceId === "campus-building"
  ) {
    dispatch(actionMapSourceDataLoaded(true));
  }
};
